import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { makePostCallForGenericAuthStatus } from "../apis/makePostCallForGenericAuthStatus";
import { GenericAuthStatusState } from "./useGenericAuthStatusTableData";

/**
 * Effect hook that triggers a Postgres data fetch whenever the filter payload
 * or auth status options change, but only after the initial data fetch has completed.
 * Resets the table context's lastBatchFetched to 0 on each new filter/search query.
 *
 * @param filterPayload - Current search parameters or null if not yet computed
 * @param initialDataFetchingExecuted - Whether the initial batch fetch has completed
 * @param setTableData - State setter for the processed table rows
 * @param setGenericAuthStatusState - State setter for API response metadata
 * @param setPaginationResetCount - Incrementing setter to trigger pagination reset
 * @param tabId - Auth status tab identifier for scoping table context
 */
export const useFilterAndSearchEffect = (
  filterPayload: SearchForMedicalPaOrdersFromPostgresParams | null | undefined,
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
  tabId: string,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext } = useTablesContext();
  const isFirstPayload = useRef(true);

  useEffect(() => {
    if (
      filterPayload &&
      initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      if (isFirstPayload.current) {
        isFirstPayload.current = false;
        return;
      }

      makePostCallForGenericAuthStatus(
        filterPayload,
        authStatusOptions,
        setGenericAuthStatusState,
        setTableData,
        setPaginationResetCount,
      );
      setTableDataForContext(
        `${TableNames.GENERIC_AUTH_STATUS_ORDERS}_${tabId}`,
        {
          lastBatchFetched: 0,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPayload, authStatusOptions]);
};
